// "Export to SharePoint" button for an approved suitability-letter agent
// task (issue #7 / ADR-0007). Sits next to the existing "Download letter
// (PDF)" affordance. Manual trigger, not auto-on-approve. Hidden entirely
// when SharePoint is unconfigured — no demo-mode fallback.

import { useState } from 'react';
import { Upload, Check, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { api, apiFetch } from '@/lib/api';
import { useSharePointStatus } from '@/lib/use-sharepoint-status';
import type { VaultRow, AgentTask } from '@septima/contract';

interface Props {
    task: AgentTask;
}

export function ExportToSharePointButton({ task }: Props) {
    const { status, loading } = useSharePointStatus();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exported, setExported] = useState<VaultRow | null>(null);

    // Hide entirely while we don't know the status, and when known to be
    // unconfigured. This is the ADR-0007 "honest unconfigured state":
    // unconfigured → no UI surface at all.
    if (loading) return null;
    if (!status?.configured) return null;

    // Only the SCRIVENER suitability-letter draft renders a SharePoint-bound
    // PDF (PRD #261 follow-up: keyed on the produced artefact type).
    if (task.artefactType !== 'suitability-letter-draft') return null;
    const clientName = task.clientName ?? 'Client';

    const downloadUrl = `/api/docs/suitability-letter/${task.id}`;

    const handleClick = async () => {
        if (busy) return;
        setError(null);
        setBusy(true);
        try {
            // Fetch the existing server-rendered PDF so we have a single render
            // path. The bytes round-trip browser → server is wasteful (PDF is
            // KB-to-low-MB; invisible at our volumes) and keeps the SharePoint
            // controller decoupled from the PDF templates.
            const res = await apiFetch(downloadUrl);
            if (!res.ok) {
                throw new Error(`Could not fetch rendered PDF (HTTP ${res.status}).`);
            }
            const pdfBytes = new Uint8Array(await res.arrayBuffer());
            const pdfBase64 = bytesToBase64(pdfBytes);

            const versionStamp = versionStampFromTask(task);
            const exportRes = await api.sharePoint.exportPdf({
                body: {
                    producerId: task.id,
                    versionStamp,
                    taxonomy: 'suitability_letter',
                    title: `Suitability Letter - ${clientName}`,
                    clientId: task.clientId ?? null,
                    clientName: task.clientName ?? null,
                    pdfBase64,
                },
            });
            if (exportRes.status === 200) {
                setExported(exportRes.body.vaultRow);
            } else if (exportRes.status === 503) {
                setError(exportRes.body.error);
            } else {
                setError('Export failed.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Export failed.');
        } finally {
            setBusy(false);
        }
    };

    if (exported) {
        return (
            <div className="flex flex-col gap-1">
                <a
                    href={exported.webUrl ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                        'h-segmented px-3 rounded-button text-annotation font-medium',
                        'inline-flex items-center justify-center gap-1.5',
                        'border border-[rgb(var(--color-rag-green)/0.3)]',
                        'text-[rgb(var(--color-rag-green))]',
                        'hover:bg-[rgb(var(--color-rag-green)/0.06)]',
                        'transition-colors duration-card-hover ease-swiss',
                    )}
                >
                    <Check className="w-3 h-3" strokeWidth={2} />
                    Exported to SharePoint
                    {exported.webUrl && <ExternalLink className="w-3 h-3" strokeWidth={2} />}
                </a>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1">
            <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleClick()}
                busy={busy}
                icon={<Upload className="w-3 h-3" strokeWidth={2} />}
            >
                {busy ? 'Exporting…' : 'Export to SharePoint'}
            </Button>
            {error && <p className="text-annotation text-[rgb(var(--color-rag-red))]">{error}</p>}
        </div>
    );
}

// version_stamp keys the vault row's identity per ADR-0007 (idempotency on
// re-export of the same letter version). When the agent output carries an
// explicit versionStamp we use it; otherwise we synthesise one from the
// task id + reviewed-at timestamp so a re-run of the agent yields a new
// row rather than overwriting in place.
function versionStampFromTask(task: AgentTask): string {
    const reviewedAt = (task as { reviewedAt?: string }).reviewedAt;
    return reviewedAt ? `${task.id}-${reviewedAt}` : task.id;
}

function bytesToBase64(bytes: Uint8Array): string {
    // chunk to avoid stack-blowing String.fromCharCode(...big array)
    const CHUNK = 8192;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.subarray(i, i + CHUNK);
        binary += String.fromCharCode(...slice);
    }
    return btoa(binary);
}
